/**
 * English string table — the single source of truth for all UI copy.
 * Keys follow the pattern  <scope>.<name>  (dot-separated, camelCase).
 *
 * "as const" makes every value a string literal so StringKey is exact.
 */
export const en = {
  /* ---- Navigation ---- */
  "nav.library":        "Library",
  "nav.projects":       "Projects",
  "nav.help":           "Help",
  "nav.admin":          "Admin",
  "nav.newTranscript":  "New Transcript",
  "nav.signOut":        "Sign Out",
  "nav.addProject":     "Add project",
  "nav.unassigned":     "Unassigned",
  "nav.collapseProjects": "Collapse projects",
  "nav.expandProjects":   "Expand projects",
  "nav.toggleTheme":      "Toggle theme",
  "nav.signOutAria":      "Sign out",
  "nav.sidebarAriaLabel": "Sidebar navigation",
  "nav.openNavMenu":      "Open navigation menu",

  /* ---- Library page ---- */
  "library.searchPlaceholder":  "Search transcripts…",
  "library.filterAll":          "All",
  "library.filterReady":        "Ready",
  "library.filterInProgress":   "In progress",
  "library.filterArchived":     "Archived",
  "library.sortNewest":         "Newest first",
  "library.sortOldest":         "Oldest first",
  "library.emptyTitle":         "No transcripts yet",
  "library.emptyDesc":          "Upload a recording to get started.",
  "library.archivedBadge":      "Archived",
  "library.heading":            "Transcripts",
  "library.headingArchived":    "Archived transcripts",
  "library.newTranscript":      "New transcript",
  "library.archived":           "Archived",
  "library.backToLibrary":      "Back to transcripts",
  "library.sortLabel":          "Sort",
  "library.projectLabel":       "Project",
  "library.lockLabel":          "Lock",
  "library.allProjects":        "All projects",
  "library.unassigned":         "Unassigned",
  "library.allLock":            "All",
  "library.lockedOnly":         "Locked only",
  "library.unlockedOnly":       "Unlocked only",
  "library.noMatches":          "No transcripts match the current filters.",
  "library.noArchived":         "No archived transcripts.",
  "library.rename":             "Rename",
  "library.reassignProject":    "Reassign project",
  "library.archive":            "Archive",
  "library.restore":            "Restore",
  "library.deletePermanently":  "Delete permanently",
  "library.sortCreatedDesc":    "Created (newest first)",
  "library.sortCreatedAsc":     "Created (oldest first)",
  "library.sortTitleAsc":       "Title (A → Z)",
  "library.sortTitleDesc":      "Title (Z → A)",
  "library.sortDurationDesc":   "Duration (longest first)",
  "library.sortDurationAsc":    "Duration (shortest first)",
  "library.sortLockedFirst":    "Locked first",
  "library.sortUnlockedFirst":  "Unlocked first",
  "library.transcriptsCount":   "{n} transcripts",
  "library.transcriptCountOne": "1 transcript",

  /* ---- Status labels (stage vocabulary — spec §9) ---- */
  "status.ready":                 "Your review",
  "status.transcribing":          "Transcription",
  "status.annotating":            "Analysis",
  "status.pending":               "Briefing",
  "status.uploaded":              "Briefing",
  "status.error":                 "Error",
  "status.awaitingContextReview": "Briefing — needs you",
  "status.final":                 "Final",

  /* ---- Upload / new transcript ---- */
  "upload.dropzoneTitle":       "Drop your recording here",
  "upload.dropzoneSubtitle":    "or click to browse · audio or video",
  "upload.dropzoneInvalid":     "Only audio or video files are supported.",
  "upload.contextLabel":        "Meeting context",
  "upload.contextHelper":
    "This is the main accuracy lever — tell us who was in the meeting and what it was about.",
  "upload.contextPlaceholder":  "e.g. Product sync with Ilan and Roni. Topics: roadmap Q3, launch risks…",
  "upload.attendeesLabel":      "Attendees",
  "upload.projectLabel":        "Project",
  "upload.languageLabel":       "Primary language",
  "upload.titleLabel":          "Title",
  "upload.titlePlaceholder":    "e.g. G Electronics weekly sync",
  "upload.recordedAtLabel":     "When was this recorded?",
  "upload.recordedAtHelper":    "Used to show real-time timestamps in the transcript.",
  "upload.reviewContextLabel":  "Review parsed context before transcribing",
  "upload.reviewContextHelper": "Pause after we parse your notes, so you can fix name spellings or remove the wrong attendees before the recording is sent for transcription.",
  "upload.clearFile":           "Clear file selection",
  "upload.submitIdle":          "Upload + transcribe",
  "upload.submitCreating":      "Creating record…",
  "upload.submitDispatching":   "Dispatching to worker…",
  "upload.submitDone":          "Done",
  "upload.projectHelper":       "This meeting will use attendees + glossary from \"{name}\" and your workspace.",
  "upload.projectHelperNone":   "This meeting will use only your workspace-wide attendees + glossary.",
  "upload.submitButton":        "Start transcription",
  "upload.uploading":           "Uploading…",
  "upload.processing":          "Processing…",
  "upload.pageTitle":           "New transcript",
  "upload.errorUploadNotFinished": "The file upload didn't finish — upload the file again.",
  "upload.sniffWarning": "This doesn't look like an audio file. You can continue anyway.",
  "upload.sniffContinue":       "Continue anyway",
  "upload.sniffChooseAnother":  "Choose a different file",

  /* ---- Meeting page ---- */
  "player.following":     "Following",
  "player.follow":        "Follow",
  "meeting.lock":         "Lock",
  "meeting.unlock":       "Unlock",
  "meeting.updateAnalysis": "Update the analysis",
  "meeting.exportMd":     "Export Markdown",
  "meeting.exportJson":   "Export JSON",
  "meeting.exportDocx":   "Export Word",
  "meeting.exportAudio":  "Export Audio",
  "meeting.tabSummary":   "Summary",
  "meeting.tabTranscript":"Transcript",
  "meeting.tabReview":    "Review",
  "meeting.contextCard":       "What the transcriber was told",
  "meeting.contextDisclosure": "What the transcriber was told",
  "meeting.contextBriefing":   "The briefing built from your notes",
  "meeting.noProposals":       "No new names or terms were proposed for this meeting. Update the analysis to scan the transcript again.",
  "meeting.dangerZone":        "Danger zone",
  "meeting.deleteConfirm":"Delete this transcript permanently?",
  "meeting.archiveLabel": "Archive transcript",
  "meeting.deleteLabel":  "Delete permanently",
  "meeting.exportButton": "Export",
  "meeting.preparing":    "Preparing…",

  /* ---- Export dialog ---- */
  "export.dialogTitle":      "Export",
  "export.sectionsLabel":    "Include",
  "export.sectionTranscript":"Full meeting transcript",
  "export.sectionSummary":   "Summary & analysis",
  "export.noSummaryNote":    "No summary has been created for this transcript yet.",
  "export.formatLabel":      "Format",
  "export.formatMarkdown":   "Markdown (.md)",
  "export.formatWord":       "Word (.docx)",
  "export.formatHtml":       "HTML",
  "export.formatJson":       "JSON",
  "export.download":         "Download",
  "export.cancel":           "Cancel",

  /* ---- Summary tab ---- */
  "summary.aiLabel":      "AI summary — based on the transcript",
  "summary.overview":     "Overview",
  "summary.keyMoments":   "Key moments",
  "summary.actionItems":  "Action items",
  "summary.decisions":    "Decisions",
  "summary.emptyTitle":   "No summary yet",
  "summary.emptyDesc":    "The summary will appear once transcription and analysis complete.",
  "summary.emptyBody":    "No summary yet. Generate one — it re-reads the transcript only, nothing else changes.",
  "summary.regenerate":   "Regenerate the summary",
  "summary.generate":     "Generate the summary",
  "summary.regenerating": "Regenerating…",

  /* ---- Login ---- */
  "login.heading":              "Welcome to Timlul",
  "login.subheading":           "Sign in with your work email to continue.",
  "login.signIn":               "Sign in",
  "login.tagline":              "Hebrew + English meeting transcription. Enter your email to receive a sign-in link.",
  "login.emailLabel":           "Email address",
  "login.emailPlaceholder":     "you@example.com",
  "login.sendLink":             "Send magic link",
  "login.sending":              "Sending…",
  "login.checkInbox":           "Check your inbox",
  "login.checkInboxDetail":     "We sent a sign-in link to",
  "login.checkEmail":           "Check your email for a sign-in link.",
  "login.inviteOnly":           "This workspace is invite-only.",
  "login.inviteNeededEn":       "This email isn't on Timlul yet. Access is by invitation — please ask an administrator to invite you.",
  "login.inviteNeededHe":       "האימייל הזה עדיין לא רשום בתמלול. הגישה היא בהזמנה בלבד — אנא בקשו ממנהל המערכת להזמין אתכם.",

  /* ---- Projects ---- */
  "projects.heading":          "Projects",
  "projects.headingArchived":  "Archived projects",
  "projects.addProject":       "Add project",
  "projects.archived":         "Archived",
  "projects.backToProjects":   "Back to projects",
  "projects.emptyTitle":       "No projects yet",
  "projects.emptyDesc":        "A project groups attendees, glossary terms, and transcripts so context stays relevant per client or topic.",
  "projects.emptyArchivedTitle": "No archived projects",
  "projects.emptyArchivedDesc":  "Archived projects appear here. You can restore them at any time.",
  "projects.contextHelper":    "Flows into the context bundle the speech engine and AI see for every meeting in this project.",
  "projects.descriptionLabel": "Description",
  "projects.descriptionOptional": "(optional)",
  "projects.nameRequired":     "Name is required.",

  /* ---- Help ---- */
  "help.heading":           "Help & documentation",
  "help.gettingStarted":    "Getting started",
  "help.contextTips":       "Writing good context",
  "help.exportFormats":     "Export formats",
  "help.keyboardShortcuts": "Keyboard shortcuts",

  /* ---- Admin ---- */
  "admin.heading":          "Admin",
  "admin.users":            "Users",
  "admin.inviteUser":       "Invite user",
  "admin.auditLog":         "Audit log",
  "admin.workspaceSettings":"Workspace settings",

  /* ---- Common actions ---- */
  "common.save":    "Save",
  "common.cancel":  "Cancel",
  "common.delete":  "Delete",
  "common.archive": "Archive",
  "common.restore": "Restore",
  "common.edit":    "Edit",
  "common.close":   "Close",
  "common.loading": "Loading…",
  "common.retry":   "Retry",
  "common.confirm": "Confirm",
  "common.back":    "Back",

  /* ---- Review surface sections ---- */
  "review.sectionUnderstood":  "What the analysis understood",
  "review.sectionFlagged":     "Flagged for you",
  "review.sectionAttendees":   "Attendees",
  "review.sectionGlossary":    "Glossary",
  "review.sectionHistory":     "What we told the transcriber",
  "review.decideCount":        "{n} things to decide",
  "review.allClear":           "Nothing left to review.",
  "review.countFlag":          "{n} flag",
  "review.countGlossary":      "{n} glossary",
  "review.countAttendees":     "{n} attendees",

  /* ---- Proposed attendees + glossary cards ---- */
  "proposed.inAttendeeList":    "✓ In the project's attendee list",
  "proposed.inGlossary":        "✓ In the project's glossary",
  "proposed.removeFromProject": "Remove from project",
  "proposed.addToProject":      "Add to project",
  "proposed.assignToProject":   "Assign this meeting to a project to save these names to it.",
  "proposed.assignToProjectGlossary": "Assign this meeting to a project to save these terms to it.",
  "proposed.removedUndo":             "Removed · Undo",
  "proposed.addToAttendeeList":       "Add to project attendee list",
  "proposed.savedAndAdded":           "✓ Saved + added to project",
  "proposed.savedButNotAdded":        "Saved, but couldn't add to the attendee list: ",
  "proposed.savedNameButNotAdded":    "Saved name, but couldn't add to the attendee list: ",

  /* ---- Journey strip ---- */
  "journey.stageUpload":        "Upload",
  "journey.stageBriefing":      "Briefing",
  "journey.stageTranscription": "Transcription",
  "journey.stageAnalysis":      "Analysis",
  "journey.stageReview":        "Your review",
  "journey.stageFinal":         "Final",
  "journey.briefingActive":     "Turning your notes into the briefing the transcriber follows…",
  "journey.briefingHint":       "Usually 10–30 seconds",
  "journey.briefingNeedsYou":   "The briefing is ready for your eyes before transcription starts.",
  "journey.briefingReviewCta":  "Review the briefing ↓",
  "journey.transcriptionActive":"Turning the recording into text — exactly what was said, never rewritten…",
  "journey.transcriptionHint":  "Usually 1–3 minutes for short recordings",
  "journey.analysisActive":     "Reading the whole conversation: who said what (double-checked), suspicious wording, names & terms, and a summary…",
  "journey.analysisHint":       "Usually 30–60 seconds — this is what makes your review fast",
  "journey.reviewHeader":       "The analysis found {n} things worth your eyes.",
  "journey.reviewAllClear":     "Everything handled — nothing left to review.",
  "journey.reworkOfferLead":    "Something the analysis missed?",
  "journey.reworkSection":      "Rework a section…",
  "journey.updateAnalysis":     "Update the whole analysis…",
  "journey.updating":           "Updating…",
  "journey.lockAnyway":         "Lock anyway",
  "journey.lockClean":          "Lock this meeting",
  "journey.lockConfirm":        "{n} items still pending — lock anyway?",
  "journey.lockConfirmYes":     "Yes, lock it",
  "journey.locking":            "Locking…",
  "journey.unlocking":          "Unlocking…",
  "journey.finalLine":          "Final — locked {date} · a finished, trusted source",
  "journey.tryAgain":           "Try again",
  "journey.replaceAudio":       "Replace the audio file",
  "journey.replaceStarting":    "Starting…",
  "journey.errorBriefing":      "Preparing the briefing failed.",
  "journey.errorTranscription": "Transcription failed.",
  "journey.errorAnalysis":      "The analysis failed.",
  "journey.mobileLine":         "Stage {x} of 6 — {stage}",
  "journey.mobileLeft":         "{n} left",
  "journey.reworking":          "Reworking {from}–{to}…",
  "journey.integrityBroken":    "The transcription itself looks broken — read why and start over",
  "journey.integrityHeading":   "The analysis thinks the transcription itself is broken",
  "journey.integrityStartOverLead": "Starting over re-transcribes the audio and erases your manual work — the same guarded action as always. The evidence above is the legitimate reason to use it.",
  "journey.exportMenuLabel":    "Export options",
  "journey.lockTitle":          "Mark this meeting as final.",

  /* ---- Rework a section ---- */
  "rework.title":        "Rework this section",
  "rework.body":         "Re-checks who's speaking and flags misheard words in this stretch only. Fresh suggestions land in the review list; your own fixes anywhere are untouched.",
  "rework.hintLabel":    "Anything the analysis should know about this stretch? (optional)",
  "rework.hintPlaceholder": "e.g. 'יואב left the room ~min 35; the one speaking after is אורי'",
  "rework.costEstimate": "Rough cost: ~${cost}",
  "rework.confirm":      "Send for rework",
  "rework.sending":      "Sending…",
  "rework.readout":      "{from} → {to} · {n} paragraphs · ~{min} min of audio",
  "rework.readoutSingle": "{from} → {to} · 1 paragraph · ~{min} min of audio",
  "rework.readoutHint":  "Tap the first and last paragraph of the stretch — then send it for rework.",
  "rework.selectingHeader": "Select the section to rework",
  "rework.selectingSub": "Tap the first and last paragraph of the stretch — then send it for rework.",
  "rework.continue":     "Continue",
  "rework.clear":        "Clear selection",
  "rework.capStart":     "Start",
  "rework.capEnd":       "End",
  "rework.capSingle":    "Section",
  "rework.entry":        "Rework a section",

  /* ---- Common with interpolation ---- */
  "common.nItems":  "{n} items",
  "common.xOfY":    "{x} of {y}",

  /* ---- Slice 2 — variant question + trust line ---- */
  "variant.possiblyKnown":        "Possibly already known",
  "variant.linkAsAttendee":       "Link as the Hebrew spelling",
  "variant.linkAsGlossary":       "Link as the alternate spelling",
  "variant.keepSeparate":         "Keep separate",
  "variant.linkedToast":          "Linked \"{proposed}\" as a spelling of \"{match}\"",
  "variant.keptSeparateToast":    "Kept separate — will be proposed as new",
  "review.recognisedLine":        "{n} already-known recognised automatically",
  "review.alreadyInAttendees":    "Already in the project's attendee list",
  "review.alreadyInGlossary":     "Already in the project's glossary",

  /* ---- Slice 4 Task 1 — summon-proof inline transcript ---- */
  "review.showTranscriptHere":    "Show the transcript here",
  "review.hideTranscript":        "Hide",
  "review.playFrom":              "▶ play from {ts}",

  /* ---- Common (shared across many surfaces — see conventions note above) ---- */
  "common.saving":          "Saving…",
  "common.add":             "Add",
  "common.dismiss":         "Dismiss",
  "common.undo":            "Undo",
  "common.nameLabel":       "Name",
  "common.remove":          "Remove",
  "common.mergeInto":       "Merge into…",
  "common.unlockMeetingFirst": "Unlock the meeting first.",
  "common.unlockTranscriptFirst": "Unlock the transcript first.",
  "common.clickToRename":   "Click to rename",

  /* ---- Shared dialog fragments (Attendee / Glossary form dialogs) ---- */
  "dialogs.projectSelectPlaceholder": "— Select a project —",
  "dialogs.projectHelper":            "Flows into meetings in this project only. A project is required.",
  "dialogs.aliasesLabel":             "Aliases (comma-separated)",
  "dialogs.notesLabelOptional":       "Notes (optional)",

  /* ---- Attendee form dialog ---- */
  "dialogs.attendee.projectRequired": "A project is required. Select a project for this attendee.",
  "dialogs.attendee.duplicateName":   'An attendee named "{name}" already exists in this workspace. Close this dialog and edit the existing row, or pick a different name.',
  "dialogs.attendee.titleCreate":     "Add attendee",
  "dialogs.attendee.titleEdit":       "Edit attendee",
  "dialogs.attendee.namePlaceholder": "e.g. Yoav Levin",
  "dialogs.attendee.roleLabel":       "Role (optional)",
  "dialogs.attendee.rolePlaceholder": "e.g. Cloudflare ops",
  "dialogs.attendee.aliasesPlaceholder": "e.g. יואב, Yoav",
  "dialogs.attendee.aliasesHelper":   "Alternate spellings. Hebrew/English variants of the same person. Claude sees them all when attributing speakers.",
  "dialogs.attendee.notesPlaceholder": "e.g. CTO at Limetra; usually leads infra discussions",

  /* ---- Glossary form dialog ---- */
  "dialogs.glossary.termRequired":    "Term is required.",
  "dialogs.glossary.projectRequired": "A project is required. Select a project for this term.",
  "dialogs.glossary.duplicateTerm":   'A glossary term "{term}" already exists in this workspace. Close this dialog and edit the existing row, or pick a different term.',
  "dialogs.glossary.titleCreate":     "Add glossary term",
  "dialogs.glossary.titleEdit":       "Edit glossary term",
  "dialogs.glossary.termLabel":       "Term",
  "dialogs.glossary.termPlaceholder": "e.g. DHL",
  "dialogs.glossary.aliasesPlaceholder": "e.g. די.אייץ.אל, DHL Express",
  "dialogs.glossary.aliasesHelper":   "Alternate spellings. Hebrew/English variants of the same term. Claude sees them all when reviewing transcripts.",
  "dialogs.glossary.notesPlaceholder": "e.g. International courier company; comes up in shipping discussions",

  /* ---- Project create/edit dialog (projects.* has the shared bits) ---- */
  "projectDialog.titleEdit":       "Edit project",
  "projectDialog.namePlaceholder": "e.g. Yoav follow-ups",
  "projectDialog.descPlaceholder": "e.g. Ongoing infrastructure conversations with Yoav at Limetra. Topics: Cloudflare, DNS, deployment.",

  /* ---- Back-to-top button ---- */
  "backToTop.label": "Back to top",

  /* ---- Correction popover ---- */
  "correctionPopover.emptyError":    "Corrected text can't be empty.",
  "correctionPopover.identicalError": "Corrected text is identical to the original.",
  "correctionPopover.saved":         "✓ Correction saved",
  "correctionPopover.originalLabel": "Original:",
  "correctionPopover.textPlaceholder": "Corrected text",
  "correctionPopover.promoteLabel":  "Promote to glossary",

  /* ---- Danger zone (resubmit / start-over) ---- */
  "dangerZone.cannotStartOver":  "Cannot start over while status is {status}.",
  "dangerZone.readTranscriptsError": "couldn't read transcripts: {msg}",
  "dangerZone.readCorrectionsError": "couldn't read corrections: {msg}",
  "dangerZone.readMeetingError":     "couldn't read meeting: {msg}",
  "dangerZone.snapshotError":        "couldn't save a safety snapshot of your current work -- resubmit not fired. {msg}",
  "dangerZone.summary":              "Danger zone — destructive actions",
  "dangerZone.startOverLabel":       "Start over",
  "dangerZone.startOverBody":        " throws away the current transcript and re-transcribes the original audio (~$0.14). All speaker labels, text corrections, speaker assignments, and AI suggestions on this transcript are erased permanently. Only do this if the current transcript is so broken that starting over is faster than fixing it by hand. When the analysis reports the transcription itself came back broken, that warning is the legitimate trigger for this.",
  "dangerZone.startOverButton":      "Start over (~$0.14)",
  "dangerZone.eraseDialogTitle":     "Erase this transcript and start over?",
  "dangerZone.eraseDialogBody":      "Starting over re-runs transcription on the original audio (~$0.14). The following are erased and cannot be recovered automatically:",
  "dangerZone.itemSpeakerLabels":        "Manual speaker label changes",
  "dangerZone.itemTextCorrections":      "Text corrections you typed",
  "dangerZone.itemSpeakerAttributions":  "Speaker attributions you set on parts of the transcript",
  "dangerZone.itemClaudeSuggestions":    "Claude suggestions you accepted or dismissed",
  "dangerZone.snapshotNote":         "A snapshot of your current work is saved to the activity log before the resubmit fires, so a future restore feature could bring it back — but no restore UI exists today. Treat this as permanent.",
  "dangerZone.savingSnapshot":       "Saving snapshot…",
  "dangerZone.confirmErase":         "Yes, erase and start over",

  /* ---- Lock badge ---- */
  "lockBadge.locked": "Locked",

  /* ---- Tabs (generic) ---- */
  "tabs.ariaLabel": "Tabs",

  /* ---- Audio player ---- */
  "audioPlayer.playPause":    "Play / pause",
  "audioPlayer.playheadTime": "Playhead time",

  /* ---- Recorded-time editor ---- */
  "recordedTimeEditor.label":    "Recorded",
  "recordedTimeEditor.ariaLabel": "Recorded date and time",

  /* ---- Permanent-delete confirm dialog ---- */
  "confirmDelete.transcriptMessage": "This permanently deletes the audio recording and the entire transcript — including all edits, flags, and history. This cannot be undone.",
  "confirmDelete.projectMessage":    "This permanently deletes the project and its attendees and glossary. Its transcripts are NOT deleted — they become Unassigned. This cannot be undone.",
  "confirmDelete.userMessage":       "This permanently deletes the user's account and all their data — every transcript, recording, and their workspace. This cannot be undone.",
  "confirmDelete.title":             "Delete permanently?",
  "confirmDelete.prompt":            "Type the {kind} to confirm:",
  "confirmDelete.kindTitle":         "title",
  "confirmDelete.kindProjectName":   "project name",
  "confirmDelete.kindEmail":         "email",
  "confirmDelete.deleting":          "Deleting…",

  /* ---- ReviewItemRow ---- */
  "reviewItemRow.jumpToAudio":      "Jump to this moment in the audio",
  "reviewItemRow.hideEvidence":     "hide evidence",
  "reviewItemRow.evidence":         "evidence",
  "reviewItemRow.saveFailedRetry":  "Couldn't save: {error}. Click the action again to retry.",
  "reviewItemRow.correctedNote":    "Corrected via the corrections layer.",
  "reviewItemRow.dismissedPill":    "✗ Dismissed",
  "reviewItemRow.appliedPill":      "✓ Applied: {name}",
  "reviewItemRow.correctedPill":    "Corrected",
  "reviewItemRow.reviewedPill":     "✓ Reviewed",
  "reviewItemRow.undoAction":       "undo",
  "reviewItemRow.undoRestoreTitle": "Undo; restore to pending",
  "reviewItemRow.undoRemoveAttributionTitle": "Undo; remove the applied speaker attribution",
  "reviewItemRow.applyTitle":       'Apply "{name}" as the speaker for this span',
  "reviewItemRow.useAction":        "use {name}",
  "reviewItemRow.editSpeakerTitle": "Jump to the turn so you can edit the speaker name inline",
  "reviewItemRow.fixSpeakerAction": "fix speaker…",
  "reviewItemRow.fixTextTitle":     "Fix the text in this span; the correction overlays at render",
  "reviewItemRow.correctAction":    "correct",
  "reviewItemRow.dismissTitle":     "Mark this as not a problem",
  "reviewItemRow.dismissAction":    "dismiss",

  /* ---- ReviewPanel ---- */
  "reviewPanel.reviewedNothingToFlag": "The analysis reviewed this — nothing to flag.",
  "reviewPanel.notRunYet":             "The analysis hasn't run on this transcript yet.",
  "reviewPanel.checksComplete":        "Speaker attribution and mishearing checks completed with no items to address.",
  "reviewPanel.runAnalysisDesc":       "Run the analysis: who said what with reasoning, an independent second check for swaps, and a sweep for suspicious mishearings.",
  "reviewPanel.reRunTitle":            "Re-run the analysis on the existing transcript.",
  "reviewPanel.reviewLabel":           "Review",
  "reviewPanel.notesPrefix":           " — Claude's notes on this transcript · ",
  "reviewPanel.itemCountOne":          "1 item",
  "reviewPanel.itemCountMany":         "{n} items",
  "reviewPanel.toAddressWithBreakdown": " to address before lock ({breakdown})",
  "reviewPanel.toAddressPlain":        " to address before lock",
  "reviewPanel.hideDetails":           "Hide details ▴",
  "reviewPanel.showDetails":           "Show details ▾",

  /* ---- SpeakerOverride popover ---- */
  "speakerOverride.justThisPartTitle": "Use this name only for this stretch of the meeting (you can extend below).",
  "speakerOverride.justThisPart":      "Just this part",
  "speakerOverride.wholeMeetingTitle": "Use this name everywhere this voice appears in the meeting.",
  "speakerOverride.wholeMeeting":      "Whole meeting",
  "speakerOverride.saved":             "✓ Saved",
  "speakerOverride.unmapPlaceholder":  "(empty = unmap)",
  "speakerOverride.namePlaceholder":   "Speaker name",
  "speakerOverride.nameRequired":      "A name is required. To remove it from this stretch, use the undo button on the turn.",
  "speakerOverride.toEnd":             "To end",
  "speakerOverride.turnsByLabelOne":   "1 turn by [spk_{label}],",
  "speakerOverride.turnsByLabelMany":  "{n} turns by [spk_{label}],",
  "speakerOverride.singleTurnLabel":   "1 turn by [spk_{label}]",
  "speakerOverride.untilTime":         "until {time}",
  "speakerOverride.shrinkTitle":       "Shrink the span (remove the last same-label turn)",
  "speakerOverride.extendTitle":       "Extend the span to the next turn by this label (skips intervening different-label turns)",
  "speakerOverride.noMoreTurnsTitle":  "No more turns by this label",
  "speakerOverride.dropToSingleTitle": "Currently extending to the end. Click to drop back to a single turn.",
  "speakerOverride.extendToAllTitle":  "Extend the span to cover ALL remaining turns by this label.",

  /* ---- Reset-to-original confirmation dialog ---- */
  "resetDialog.title":              "Reset this paragraph to the original transcript?",
  "resetDialog.willRemove":         "This will remove all your changes to this paragraph:",
  "resetDialog.textCorrectionOne":  "1 text correction",
  "resetDialog.textCorrectionsMany": "{n} text corrections",
  "resetDialog.speakerOverrideOne": "1 speaker override",
  "resetDialog.speakerOverridesMany": "{n} speaker overrides",
  "resetDialog.paragraphSplitOne":  "1 paragraph split",
  "resetDialog.paragraphSplitsMany": "{n} paragraph splits",
  "resetDialog.bookmarkOne":        "The bookmark on this paragraph",
  "resetDialog.bookmarksMany":      "{n} bookmarks on this paragraph",
  "resetDialog.undoNote":           "This action can be undone via the global Undo button afterward.",
  "resetDialog.keepChanges":        "Keep changes",
  "resetDialog.resetting":          "Resetting…",
  "resetDialog.yesReset":           "Yes, reset",

  /* ---- Meeting-level event descriptions (Undo button label source) ---- */
  "events.renamedTo":               'renamed to "{title}"',
  "events.renamedSpeaker":          "renamed speaker to {name}",
  "events.assignedSpeaker":         "assigned speaker to {name}",
  "events.removedSpeakerAssignment": "removed speaker assignment",
  "events.paragraphSplit":          "paragraph split",
  "events.paragraphRejoin":         "paragraph rejoin",
  "events.textCorrection":          "text correction",
  "events.undidCorrection":         "undid correction",
  "events.bookmarkedParagraph":     "bookmarked paragraph",
  "events.removedBookmark":         "removed bookmark",
  "events.resetParagraphOne":       "reset paragraph (1 item restored)",
  "events.resetParagraphMany":      "reset paragraph ({n} items restored)",
  "events.editSessionOne":          "paragraph edit (1 change)",
  "events.editSessionMany":         "paragraph edit ({n} changes)",

  /* ---- Transcript tab (MeetingReview) ---- */
  "transcriptTab.heading":            "Transcript",
  "transcriptTab.undoTitle":          "Undo: {event}",
  "transcriptTab.undoButton":         "↩ Undo: {event}",
  "transcriptTab.undoing":            "Undoing…",
  "transcriptTab.hideMyEditsTitle":   "Bring back your corrections, splits, and speaker overrides. (Or hold Alt to peek the other view.)",
  "transcriptTab.showRawTitle":       "Show the raw transcript before your edits. Hold Alt to peek temporarily.",
  "transcriptTab.showMyEdits":        " Show my edits",
  "transcriptTab.hideMyEdits":        " Hide my edits",
  "transcriptTab.reworkButtonTitle":  "Tap the first and last paragraph of a stretch to re-check it.",
  "transcriptTab.transcriptRowMissing": "Transcript row missing.",
  "transcriptTab.transcriptionWillStart": "Transcription will start after you submit the context above.",
  "transcriptTab.transcriptNotReady": "Transcript not ready yet — this page updates automatically.",
  "transcriptTab.noTokens":           "No tokens.",
  "transcriptTab.directionToggleTitle": "Click to flip the transcript's reading direction. The system picks a default from the dominant language; you can override it.",
  "transcriptTab.directionRtl":       "Right-to-left",
  "transcriptTab.directionLtr":       "Left-to-right",
  "transcriptTab.directionAuto":      "Text direction: {dir} (auto)",
  "transcriptTab.directionOverride":  "Text direction: {dir} (you set)",
  "transcriptTab.editParagraphTitle": "Edit this paragraph",
  "transcriptTab.splitParagraphTitle": "Split this paragraph",
  "transcriptTab.removeBookmarkTitle": "Remove the bookmark from this paragraph",
  "transcriptTab.addBookmarkTitle":   "Bookmark this paragraph for later",
  "transcriptTab.clearEditsTitle":    "Clear all edits in this paragraph back to the original transcript (no DB write — press Save or Cancel to confirm)",
  "transcriptTab.resetToOriginal":   "Reset to original",
  "transcriptTab.splitInstructions": "Click a word where you want to split this paragraph. The split will start at that word.",
  "transcriptTab.bookmarkTitle":     "Your bookmark — click to jump here",
  "transcriptTab.bookmarkAria":      "Bookmarked paragraph",
  "transcriptTab.flagTitle":         "Claude flagged this — click to review",
  "transcriptTab.flagAria":          "Claude has a note about this paragraph",
  "transcriptTab.correctedFromTitle": "Corrected from: {text}",
  "transcriptTab.undoCorrectionTitle": "Undo this correction (preserved as dismissed in audit trail)",

  /* ---- Speaker label / span-override rendering ---- */
  "speakerLabel.notAttributed":       "Not attributed",
  "speakerLabel.notAttributedTitle":  "Claude didn't attribute speaker {speaker}. Click to assign manually.",
  "speakerLabel.confidenceTitle":     "Confidence {pct}%",
  "speakerLabel.lowConfidenceTitle":  "Low confidence {pct}% — please verify",
  "speakerLabel.removeAssignmentTitle": "Remove this assignment and restore Claude's original speaker",
  "speakerLabel.claudeSuggestionTitle": "Claude suggestion {when}{confidencePart} — original label spk_{rawLabel}{reasoningPart}",
  "speakerLabel.claudeAttributionTitle": "Claude attribution {when}{confidencePart} — original label spk_{rawLabel}{reasoningPart}",
  "speakerLabel.assignedByYouTitle":  "Assigned by you {when} — original label spk_{rawLabel}",
  "speakerLabel.confidenceFragment":  " (confidence {conf})",
  "speakerLabel.reasoningFragment":   "\n\n{reasoning}",

  /* ---- Split-turn header (paragraph split second half) ---- */
  "splitHeader.renameTitle":       "Click to rename this speaker",
  "splitHeader.setWhenSplitTitle": "Speaker name set when this paragraph was split",
  "splitHeader.unnamedSpeaker":    "Unnamed speaker",
  "splitHeader.rejoinTitle":       "Remove this split and merge the two paragraphs back together",
  "splitHeader.rejoin":            "Rejoin",

  /* ---- Meeting project-assignment control (MeetingReview) ---- */
  "meetingAssign.projectLabel":        "Project:",
  "meetingAssign.newProjectNamePlaceholder": "New project name",
  "meetingAssign.creating":            "Creating…",
  "meetingAssign.createAndAssign":     "Create & assign",
  "meetingAssign.applyTitle":          "Apply this project assignment",
  "meetingAssign.openProjectTitle":    "Open this transcript's project",
  "meetingAssign.changeProjectTitle":  "Move this transcript to a different project",
  "meetingAssign.change":              "Change",
  "meetingAssign.assignToProjectTitle": "Assign this transcript to a project",
  "meetingAssign.assignEllipsis":      "Assign…",
  "meetingAssign.assigning":           "Assigning…",
  "meetingAssign.assign":              "Assign",
  "meetingAssign.setUnassigned":       "Set to Unassigned",
  "meetingAssign.fallbackTheProject":  "the project",
  "meetingAssign.newProjectOption":    "+ New project…",
  "meetingAssign.fallbackProject":     "project",

  /* ---- Pre-Soniox context review card ---- */
  "contextReview.title":              "Review before transcribing",
  "contextReview.intro":              "We've parsed your notes into a context bundle. Fix any name spelling issues, remove wrong attendees, or adjust the prose before the recording is sent for transcription. What you see here is what the transcription engine will use.",
  "contextReview.meetingDetailsLabel": "Meeting details",
  "contextReview.keyPlaceholder":     "key",
  "contextReview.valuePlaceholder":   "value",
  "contextReview.namesTermsLabel":    "Names and terms",
  "contextReview.namesTermsHelper":   "(words the transcription engine should spell consistently)",
  "contextReview.editTermTitle":      "Click to edit this term",
  "contextReview.removeTermTitle":    "Remove this term",
  "contextReview.addTermPlaceholder": "Add a name or term, press Enter",
  "contextReview.contextNotesLabel":  "Context notes",
  "contextReview.contextNotesHelper": "(background that helps the engine understand who was in the meeting and what it was about)",
  "contextReview.sendAsIsTitle":      "Send to transcription with this context as-is",
  "contextReview.sending":            "Sending…",
  "contextReview.submitAsIs":         "Submit as-is",
  "contextReview.submitting":         "Submitting…",
  "contextReview.submitToTranscription": "Submit to transcription →",

  /* ---- Re-annotate ("Update the analysis") dialog ---- */
  "reannotate.description":           "Re-runs the full analysis on the existing transcript — who said what (with an independent second check), suspicious wording, names & terms, and a fresh summary. The audio and the transcript text aren't touched, and your own fixes survive. Cost: ~$0.25–0.50.",
  "reannotate.extraContextLabel":     "Anything the analysis should know? (optional)",
  "reannotate.extraContextPlaceholder": "e.g. 'the second speaker is the woman' or 'יואב is the one talking about Fox News'",

  /* ---- Meeting review page misc ---- */
  "meetingReview.audioFailedTitle":   "Audio failed to load.",

  /* ---- Upload / new transcript (additions) ---- */
  "upload.uploadingPercent":  "Uploading… {progress}%",
  "upload.r2UploadFailed": "R2 upload failed: {status} {text}",
  "upload.r2NetworkError": "R2 upload network error",

  /* ---- Projects page (additions) ---- */
  "projects.archiveConfirm": 'Archive project "{name}"? It moves out of your active projects but nothing is lost — its attendees, glossary, and transcripts stay, and you can Restore it anytime from the Archived view.',
  "projects.archiveFailed":  "Archive failed: {msg}",
  "projects.restoreFailed":  "Restore failed: {msg}",

  /* ---- Library (additions) ---- */
  "library.untitled":   "(untitled)",
  "library.addedDate":  "Added {date}",

  /* ---- Project detail page ---- */
  "projectDetail.notFound":              "Project not found.",
  "projectDetail.archiveConfirm":        'Archive project "{name}"? It moves out of your active projects but nothing is lost — its attendees, glossary, and transcripts stay, and you can Restore it anytime from the Projects → Archived view.',
  "projectDetail.deleteAttendeeConfirm": 'Delete attendee "{name}"? This permanently removes them and won\'t be re-suggested for this project.',
  "projectDetail.deleteGlossaryConfirm": 'Delete glossary term "{term}"? This permanently removes it and won\'t be re-suggested for this project.',
  "projectDetail.mergeAttendeeConfirm":  'Fold "{source}" into "{target}"? Its name and aliases move onto "{target}", and the "{source}" row is removed.',
  "projectDetail.mergeGlossaryConfirm":  'Fold "{source}" into "{target}"? Its spelling and aliases move onto "{target}", and the "{source}" row is removed.',
  "projectDetail.unignoreFailed":        "Un-ignore failed: {msg}",
  "projectDetail.untitledTranscript":    "Untitled transcript {date}",
  "projectDetail.moveConfirm":           'Move this meeting to "{name}"?',
  "projectDetail.unassignConfirm":       'Unassign this meeting from "{name}"?',
  "projectDetail.archivedBanner":        "This project is archived. Its attendees + glossary are preserved but don't flow into new meetings. Read only.",
  "projectDetail.clickToEditDescTitle":  "Click to edit description",
  "projectDetail.addDescriptionPlaceholder": "Add a description (click to edit)…",
  "projectDetail.sectionsAriaLabel":     "Project sections",
  "projectDetail.transcriptsTab":        "Transcripts ({n})",
  "projectDetail.attendeesTab":          "Attendees ({n})",
  "projectDetail.glossaryTab":           "Glossary ({n})",
  "projectDetail.transcriptsHeading":    "Transcripts",
  "projectDetail.emptyTranscriptsPre":   "No transcripts in this project yet. Upload one from ",
  "projectDetail.emptyTranscriptsMid":   " and pick this project from the picker, or reassign an existing transcript from the ",
  "projectDetail.transcriptsListLink":   "Transcripts list",
  "projectDetail.colTitle":              "Title",
  "projectDetail.colStatus":             "Status",
  "projectDetail.colDuration":           "Duration",
  "projectDetail.colMeetingDate":        "Meeting date",
  "projectDetail.moveToProjectTitle":    "Move to a different project",
  "projectDetail.attendeesHeading":      "Attendees",
  "projectDetail.addedSinceVisit":       "· {n} added since your last visit",
  "projectDetail.addAttendeeButton":     "+ Add attendee",
  "projectDetail.noAttendeesPre":        "No attendees scoped to this project yet. ",
  "projectDetail.clickAddAttendeeHint":  'Click "Add attendee" to add the people who appear in this project\'s meetings.',
  "projectDetail.colName":               "Name",
  "projectDetail.colRole":               "Role",
  "projectDetail.colAliases":            "Aliases",
  "projectDetail.colNotes":              "Notes",
  "projectDetail.newBadge":              "new",
  "projectDetail.mergeIntoEmptyAttendee": "No other attendees in this project",
  "projectDetail.foldAttendeeTitle":     "Fold this person into another row (same person, different spelling)",
  "projectDetail.foldTermTitle":         "Fold this term into another row (same term, different spelling)",
  "projectDetail.glossaryHeading":       "Glossary",
  "projectDetail.colTerm":               "Term",
  "projectDetail.scanning":              "Scanning…",
  "projectDetail.findDuplicates":        "Find duplicates",
  "projectDetail.addTermButton":         "+ Add term",
  "projectDetail.scanFailed":            "Couldn't scan — try again.",
  "projectDetail.possibleDuplicates":    "Possible duplicates",
  "projectDetail.noDuplicatesFound":     "No duplicates found.",
  "projectDetail.mergeGroupButton":      "Merge…",
  "projectDetail.noGlossaryPre":         "No glossary terms scoped to this project yet. ",
  "projectDetail.clickAddTermHint":      'Click "Add term" to add proper nouns + industry terms specific to this project.',
  "projectDetail.mergeIntoEmptyTerm":    "No other terms in this project",
  "projectDetail.ignoredButton":         "Ignored ({n})",
  "projectDetail.kindPerson":            "person",
  "projectDetail.kindTerm":              "term",
  "projectDetail.unignoreButton":        "Un-ignore",
  "projectDetail.archiveProjectButton":  "Archive project",

  /* ---- Admin ---- */
  "admin.userFilter.clear":       "Clear user filter",
  "admin.userFilter.searchPlaceholder": "Type to search by email…",
  "admin.userFilter.ariaLabel":   "Filter by user",
  "admin.userFilter.noMatches":   "No matching users",
  "admin.users.heading":          "Users",
  "admin.users.invitePlaceholder": "Invite by email",
  "admin.users.inviting":         "Inviting…",
  "admin.users.invite":           "Invite",
  "admin.users.emptyTitle":       "No users yet",
  "admin.users.emptyDesc":        "Invite someone to get started.",
  "admin.users.colEmail":         "Email",
  "admin.users.colJoined":        "Joined",
  "admin.users.colTranscripts":   "Transcripts",
  "admin.users.colStorage":       "Storage",
  "admin.users.colRuns":          "Runs",
  "admin.users.colSpend":         "Spend",
  "admin.users.colStatus":        "Status",
  "admin.users.colActions":       "Actions",
  "admin.audit.emptyTitle":       "No events yet",
  "admin.audit.emptyDesc":        "Actions will appear here as users work.",
  "admin.audit.colWhen":          "When",
  "admin.audit.colAction":        "Action",
  "admin.audit.colWho":           "Who",
  "admin.audit.colTranscript":    "Transcript",
  "admin.audit.colCost":          "Cost",
  "admin.nav.ariaLabel":          "Admin navigation",
  "admin.nav.overview":           "Overview",
  "admin.nav.users":              "Users",
  "admin.nav.auditLog":           "Audit log",

  /* ---- Meeting header / player dock / summary tab / misc components ---- */
  "meetingHeader.backToLibrary":  "Back to library",
  "playerDock.playbackSpeed":    "Playback speed",
  "summaryTab.jumpToTranscript": "Jump to this moment in the transcript",
  "meetingSynopsis.ariaLabel":   "Attribution synopsis",
  "meetingSynopsis.label":       "Attribution synopsis",
  "meetingSynopsis.hide":        "Hide",
  "meetingSynopsis.show":        "Show",
  "editableTitle.ariaLabel":     "Transcript title",
  "editableTitle.untitledNoDate": "Untitled transcript",
  "editableTitle.ariaLabelFull": "Transcript title: {title}. Click to rename.",

  /* ---- Help page ---- */
  "help.pageTitle":               "Help",
  "help.shortcutsHeading":        "Keyboard shortcuts",
  "help.shortcutUndoKeys":        "Cmd / Ctrl + Z",
  "help.shortcutUndoDesc":        "Undo the last action. Can be chained backward; works across sessions.",
  "help.shortcutAltKeys":         "Alt (hold)",
  "help.shortcutAltDesc":         "Peek the raw transcript before your edits. Release to return to your edited view. The Option key on Mac.",
  "help.shortcutEscKeys":         "Esc",
  "help.shortcutEscDesc":         "Inside edit mode: exits and keeps your changes. Inside a popover: cancels just that popover.",
  "help.shortcutEnterKeys":       "Enter",
  "help.shortcutEnterDesc":       "Inside speaker, split, or rename popovers: save and close.",
  "help.buttonsHeading":          "What the buttons mean",
  "help.editParagraphLabel":      "Edit (pencil) at the end of a paragraph",
  "help.editParagraphPre":        "Opens the paragraph in edit mode so you can fix text. The ",
  "help.editParagraphMid":        " (split) and ",
  "help.editParagraphPost":       " (bookmark) icons next to it act without entering edit mode.",
  "help.editSpeakerLabel":        "Edit (pencil) next to a speaker name",
  "help.editSpeakerDesc":         'Click to rename the speaker for this turn. Choose "Whole meeting" to rename everywhere this voice appears, or "Just this part" to cover only this stretch.',
  "help.bookmarkLabel":           "Bookmark in the page margin",
  "help.bookmarkDesc":            "A bookmark you placed on this paragraph. Click it to jump audio to that paragraph. You can bookmark as many paragraphs as you like — each bookmark stays until you click it again to remove it. Bookmarks are visible only in the app; they never appear in exports.",
  "help.dashedLineLabel":         "Dashed line down the left edge of a paragraph",
  "help.dashedLineDesc":          "You touched this paragraph (text correction, speaker override, or split). Lets you scan the transcript and see which paragraphs you have worked on.",
  "help.dottedUnderlineLabel":    "Dotted underline on text",
  "help.dottedUnderlineWord":     "word",
  "help.dottedUnderlineDesc":     "That text is a correction you made. Hover to see the original wording. Click the small close button to undo the correction.",
  "help.hideEditsLabel":          "Hide my edits / Show my edits",
  "help.hideEditsDesc":           "Toggles between your edited view and the raw transcript as it arrived. Helpful when you cannot remember what the original said. Holding Alt does the same thing temporarily without clicking.",
  "help.undoLabel":               "Undo",
  "help.undoDesc":                "Reverses the last action. If you exited an edit session with several changes, one click reverses the whole session in one go. Can be chained backward across sessions.",
  "help.lockLabel":               "Lock / Unlock",
  "help.lockDesc":                "Locking marks the transcript as final: editing affordances hide and export buttons appear. Unlock to resume editing.",
  "help.updateAnalysisLabel":     "Update the analysis",
  "help.updateAnalysisDesc":      "Re-runs the full analysis on the existing transcript — who said what (with an independent second check), suspicious wording, names & terms, and a fresh summary. Audio and verbatim text are not touched.",
  "help.resetLabel":              "Reset to original (inside edit mode)",
  "help.resetDesc":               "Removes all your changes to a single paragraph: corrections, speaker overrides, splits, and the bookmark on that paragraph. A confirmation dialog lists what will be removed. Reversible via the Undo button afterward.",
  "help.cancelEditLabel":         "Cancel (inside edit mode)",
  "help.cancelEditDesc":          "Reverses every change you made in this edit session in one shot. This is the only action that loses session work. Click-outside and Escape both behave as Save, so accidental clicks will not cost you.",
  "help.splitHeading":            "How to split a paragraph",
  "help.splitStep1Pre":           "Hover over the paragraph and click the ",
  "help.splitStep1Post":          " split icon.",
  "help.splitStep2":              "Click the word where the new speaker takes over (a hint bar shows while picking).",
  "help.splitStep3":              "Type the speaker name for the second half and click Save.",
  "help.splitStep4":              "The paragraph splits visually. You can split again to make three paragraphs — useful when one paragraph contains a quick back-and-forth between speakers.",
  "help.fixWordHeading":          "How to fix a wrong word",
  "help.fixWordStep1Pre":         "Open edit mode (click the ",
  "help.fixWordStep1Post":        " icon at the end of the paragraph).",
  "help.fixWordStep2":            "Select the word or phrase with your mouse.",
  "help.fixWordStep3":            "A correction popover opens. Type the replacement and click Save.",
  "help.fixWordStep4":            "The corrected text shows with a dotted underline. Hover to see the original.",
  "help.recoveryHeading":         "Recovery if you make a mistake",
  "help.recoverySingleLabel":     "Single action wrong:",
  "help.recoverySingleMid":       " click ",
  "help.recoverySingleTail":      " in the top toolbar. Keep clicking to chain backward.",
  "help.recoveryWholeLabel":      "Whole edit session wrong:",
  "help.recoveryWholeMid":        " while still in edit mode, click ",
  "help.recoveryWholeTail":       ". Reverses everything you did this session in one shot.",
  "help.recoveryParagraphLabel":  "Want to start a paragraph over:",
  "help.recoveryParagraphMid":    " open edit mode, click ",
  "help.recoveryParagraphTail":   ". Removes all your changes to that paragraph. Reversible via Undo if you change your mind.",
  "help.recoveryForgotLabel":     "Forgot what the original looked like:",
  "help.recoveryForgotMid":       " hold Alt or click ",
  "help.recoveryForgotHideEdits": "Hide my edits",
  "help.recoveryForgotTail":      ". Compare without scrolling.",
} as const;

export type StringKey = keyof typeof en;
